import type { SiteBrand } from '~/config/branding';
import type { FaqNavGroup } from './faqTypes';

// Navigation grouping + display order for the FAQ knowledge base, per brand.
// The left-hand navigation tree (and mobile dropdown) render these groups in
// order, and the main content renders sections in this same flattened order.
//
// [MCV-1501] The knowledge base used to be brand-agnostic: both sites rendered
// the same MedCredVault section set. The two products now answer different
// questions, so each brand gets its own content directory
// (./faq-sections/<brand>/) and its own grouping here.

// MedCredVault: credentialing, licensing, CME, compensation and records.
const mcvNavGroups: FaqNavGroup[] = [
  {
    id: 'getting-started',
    title: 'Getting Started',
    description:
      'Welcome to MedCredVault. MedCredVault is designed to help healthcare providers and healthcare organizations simplify credential management, workforce onboarding, career planning, compliance tracking, and healthcare recruiting workflows. This knowledge base is intended to serve as a provider education resource, a credentialing and compliance reference library, a workforce and compensation guide, and a platform onboarding resource. Because healthcare credentialing and workforce management can be complex, this guide is organized into sections based on common provider and organizational needs.',
    sectionIds: [
      'platform-features-overview',
      'medcredvault-overview',
      'common-questions-and-answers',
      'provider-questions',
      'facilities-and-employers',
    ],
  },
  {
    id: 'platform-data-ai',
    title: 'Platform, Data & AI',
    sectionIds: [
      'ai-and-analytics',
      'provider-ownership-and-control',
      'integration-with-healthcarelx',
      'auto-population-of-provider-data',
      'document-management',
      'cv-management-parsing-generation',
      'why-we-collect-detailed-job-information',
    ],
  },
  {
    id: 'credentialing',
    title: 'Credentialing & Verification',
    sectionIds: [
      'provider-credentialing',
      'primary-source-verification-psv',
      'caqh',
      'hospital-credentialing-privileging',
      'payer-enrollment',
      'provider-enrollment-timeline-delays',
      'provider-identity-verification-document-security',
      'npdb',
    ],
  },
  {
    id: 'licensing-compliance',
    title: 'Licensing & Compliance',
    sectionIds: [
      'medical-licensing',
      'multi-state-licensure',
      'dea-licenses',
      'telemedicine-licensing-credentialing',
      'board-certification-moc',
    ],
  },
  {
    id: 'cme-ce',
    title: 'CME & Continuing Education',
    sectionIds: [
      'cme-state-licensure-physicians',
      'cme-state-licensure-physician-assistants',
      'cme-ce-state-licensure-nurses',
      'ce-allied-health-professionals',
    ],
  },
  {
    id: 'compensation-contracts',
    title: 'Compensation & Contracts',
    sectionIds: [
      'medical-provider-compensation',
      'wrvu-non-standard-wrvu',
      'healthcare-employment-contract-basics',
      'locum-tenens-independent-contractor',
    ],
  },
  {
    id: 'records-career',
    title: 'Records, Coverage & Career',
    sectionIds: [
      'healthcare-provider-job-records',
      'procedure-logs-case-logs',
      'medical-malpractice-insurance',
      'img',
      'immigration-visa-credentialing',
      'research-publications-management',
    ],
  },
];

// HealthcareLX: the Exchange itself. Groups and blurbs come from the source
// document's four "Levels"; the leading Overview group holds its narrative
// front matter. The document's own table of contents is NOT reproduced — it
// disagrees with the body it indexes (14 entries against 13 sections, with
// Physician Groups and Staffing Agencies transposed), and this navigation tree
// is the live version of it. Section order follows the body, which is the half
// that carries the content.
const hlxNavGroups: FaqNavGroup[] = [
  {
    id: 'hlx-overview',
    title: 'Overview',
    description: 'The philosophy, mission, and architecture behind the Healthcare Labor Exchange.',
    sectionIds: ['hlx-philosophy', 'hlx-welcome', 'hlx-architecture', 'hlx-vision'],
  },
  {
    id: 'hlx-understanding',
    title: 'Understanding the Exchange',
    description:
      'Introduces the vision behind the Healthcare Labor Exchange (HLX), explains how the Exchange operates, and establishes the foundational concepts that create a transparent, standardized, intelligent, and trusted healthcare labor economy.',
    sectionIds: ['hlx-introduction', 'hlx-how-the-exchange-works'],
  },
  {
    id: 'hlx-participating',
    title: 'Participating in the Exchange',
    description:
      'How healthcare professionals, healthcare organizations, physician groups, staffing agencies, and workforce partners participate throughout the entire workforce lifecycle — from discovering opportunities and negotiating contracts to managing workforce relationships, business operations, and workforce performance.',
    sectionIds: [
      'hlx-labor-contracting',
      'hlx-professional-guide',
      'hlx-employer-guide',
      'hlx-staffing-agencies',
      'hlx-physician-groups',
      'hlx-workforce-intelligence',
      'hlx-business-operations',
    ],
  },
  {
    id: 'hlx-ecosystem',
    title: 'The Exchange Ecosystem',
    description:
      'The economic principles, Exchange architecture, and trust infrastructure that enable the Healthcare Labor Exchange to function as a transparent, standardized, intelligent, and trusted Exchange for healthcare labor.',
    sectionIds: ['hlx-labor-economics', 'hlx-medcredvault'],
  },
  {
    id: 'hlx-governance',
    title: 'Governance & Participant Success',
    description:
      'The governance standards, participant responsibilities, onboarding process, best practices, and support resources that enable every participant to succeed within the Healthcare Labor Exchange.',
    sectionIds: ['hlx-exchange-governance', 'hlx-getting-started'],
  },
];

export const faqNavGroupsByBrand: Record<SiteBrand, FaqNavGroup[]> = {
  mcv: mcvNavGroups,
  hlx: hlxNavGroups,
};

/** Flattened section id order for a brand (matches its nav tree top-to-bottom). */
export const faqSectionOrderForBrand = (brand: SiteBrand): string[] =>
  faqNavGroupsByBrand[brand].flatMap((group) => group.sectionIds);
